import { createContext, useState, ReactNode } from 'react';
import { useCookies } from 'react-cookie';
import { App, Credentials, User } from 'realm-web';
import { ForgotPasswordResult } from '../types/appTypes';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../msal-config';
import { AuthenticationResult } from '@azure/msal-browser';

const mongoAppId = process.env.REACT_APP_MONGO_APP_ID ?? '';
const realmApp = new App<Realm.DefaultFunctionsFactory, UserCustomData>(
  mongoAppId
);

export enum UserType {
  Mongo,
  AD,
}

type UserCustomData = {
  userId: string;
  name: string;
  role: string;
  email: string;
  companyId: string;
  userType: string;
  lastPasswordChange: {
    $date: {
      $numberLong: string;
    };
  };
};

type UserData = {
  type: UserType;
  id: string;
  accessToken: string | null;
  customData: UserCustomData;
};

type UserLoginParams = {
  type: UserType;
  email?: string;
  password?: string;
};

type UserContextData = {
  user?: UserData;
  fetchUser?: () => Promise<UserData | null>;
  login?: (loginParams: UserLoginParams) => Promise<UserData>;
  logout?: () => Promise<void>;
  forgotPassword?: (email: string) => Promise<ForgotPasswordResult>;
  changePassword?: (currentPassword: string, newPassword: string) => Promise<boolean>;
  resetPassword?: (
    token: string,
    tokenId: string,
    userId: string,
    password: string
  ) => Promise<boolean>;
  checkRole?: (role: string) => boolean;
  checkUserType?: (userType: string) => boolean;
};

type UserContextProviderProps = { children?: ReactNode };

export const UserContext = createContext<UserContextData>({});

export const UserContextProvider = ({ children }: UserContextProviderProps) => {
  const [user, setUser] = useState<undefined | UserData>(undefined);
  const { instance: msal } = useMsal();
  const [cookies, setCookie, removeCookie] = useCookies([
    process.env.REACT_APP_SSO_COOKIE_NAME!,
  ]);

  const mongoAcctToUserData = (
    mongoUser: User<Realm.DefaultFunctionsFactory, UserCustomData>
  ): UserData => {
    return {
      type: UserType.Mongo,
      id: mongoUser.id,
      accessToken: mongoUser.accessToken,
      customData: mongoUser.customData,
    };
  };

  const adAcctToUserData = (res: AuthenticationResult): UserData => {
    return {
      type: UserType.AD,
      id: res.account.localAccountId,
      accessToken: res.accessToken,
      customData: {
        userId: res.account.localAccountId,
        name: res.account.name ?? '',
        role: res.account.idTokenClaims?.roles?.join(',') ?? 'atlas.user',
        email: res.account.username,
        companyId: '',
        userType: '',
        lastPasswordChange: { $date: { $numberLong: Date.now().toString() } },
      },
    };
  };

  const setSsoCookie = (authedUser: UserData | null) => {
    if (
      authedUser !== null &&
      authedUser.accessToken !== null &&
      authedUser.accessToken !== ''
    ) {
      setCookie(process.env.REACT_APP_SSO_COOKIE_NAME!, authedUser.accessToken);
    } else {
      removeCookie(process.env.REACT_APP_SSO_COOKIE_NAME!);
    }
  };

  const login = async ({
    type,
    email,
    password,
  }: UserLoginParams): Promise<UserData> => {
    if (type === UserType.Mongo) return mongoLogin(email!, password!);

    return adLogin();
  };

  const mongoLogin = async (email: string, password: string) => {
    const authedUser = await realmApp.logIn(
      Credentials.emailPassword(email, password)
    );
    const user: UserData = mongoAcctToUserData(authedUser);
    setSsoCookie(user);
    setUser(user);
    return user;
  };

  const adLogin = async () => {
    const res = await msal.loginPopup({
      ...loginRequest,
      redirectUri: process.env.REACT_APP_AZUREAD_REDIRECT_URL,
    });
    const user: UserData = adAcctToUserData(res);
    setUser(user);
    return user;
  };

  const fetchUser = async () => {
    if (realmApp.currentUser && realmApp.currentUser.isLoggedIn) {
      try {
        await realmApp.currentUser.refreshAccessToken();
        await realmApp.currentUser.refreshCustomData();
        const userData = mongoAcctToUserData(realmApp.currentUser);
        setSsoCookie(userData);
        setUser(userData);
        return userData;
      } catch (error) {
        throw error;
      }
    }

    const adAccount = msal.getActiveAccount();
    if (adAccount !== null) {
      try {
        const res = await msal.acquireTokenSilent({
          ...loginRequest,
          redirectUri: '/',
        });
        const userData = adAcctToUserData(res);
        setUser(userData);
        return userData;
      } catch (error) {
        throw error;
      }
    }

    return null;
  };

  const logout = async () => {
    if (realmApp.currentUser && realmApp.currentUser.isLoggedIn) {
      try {
        await realmApp.currentUser.logOut();
      } catch (error) {
        throw error;
      }
    }

    const adAccount = msal.getActiveAccount();
    if (adAccount !== null) {
      try {
        await msal.logoutPopup({
          mainWindowRedirectUri: '/',
          account: adAccount,
        });
      } catch (error) {
        throw error;
      }
    }

    try {
      setSsoCookie(null);
      setUser(undefined);
    } catch (error) {
      throw error;
    }
  };

  const forgotPassword = async (email: string) => {
    try {
      const lowerEmail = email.toLowerCase();
      // Check if user exists
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/Registration/Check?email=${encodeURIComponent(lowerEmail)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      
      if (response.status === 200) {
        const data = await response.json();
        
        if (data.userAlreadyExists) {
          await realmApp.emailPasswordAuth.callResetPasswordFunction(
            {
              email: lowerEmail,
              password: 'Password1!',
            },
            'forgot'
          );
          return ForgotPasswordResult.Success;
        } else {
          return ForgotPasswordResult.NotFound;
        }
      } else if (response.status === 404) {
        return ForgotPasswordResult.NotFound;
      }
      
      return ForgotPasswordResult.Fail;
    } catch (error: any) {
      if (
        error !== null &&
        error.toString().toLowerCase().includes('status 404')
      ) {
        return ForgotPasswordResult.NotFound;
      }

      return ForgotPasswordResult.Fail;
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    console.log("User attempting change:", user?.id, user?.customData.email);
    try {
      if (!user || user.type !== UserType.Mongo) {
        console.log("User is not a Mongo user or user is undefined");
        return false;
      }
      
      const userEmail = user.customData.email.toLowerCase();
      
      try {
        await realmApp.logIn(Credentials.emailPassword(
          userEmail, 
          currentPassword
        ));
        console.log("User logged in successfully");
      } catch (loginError) {
        console.error("Current password verification failed:", loginError);
        return false;
      }
      
      await realmApp.emailPasswordAuth.callResetPasswordFunction(
        {
          email: userEmail,
          password: newPassword
        },
        'change'
      );
      
      const updateResult = await fetch(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_USER}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${user.accessToken}`
          },
          body: JSON.stringify({
            userId: user.id,
            newData: {
              lastPasswordChange: new Date(Date.now()).toISOString(),
            },
          }),
        }
      );
      
      if (realmApp.currentUser) {
        await realmApp.currentUser.refreshCustomData();
        const updatedUserData = mongoAcctToUserData(realmApp.currentUser);
        setUser(updatedUserData);
      }
      
      return true;
    } catch (error) {
      console.error("Password change error:", error);
      return false;
    }
  };

  const resetPassword = async (
    token: string,
    tokenId: string,
    userId: string,
    password: string
  ) => {
    try {
      const result = await fetch(
        `${process.env.REACT_APP_API_URL}${process.env.REACT_APP_API_USER}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: userId,
            newData: {
              lastPasswordChange: new Date(Date.now()).toISOString(),
            },
          }),
        }
      )
        .then(async (response) => {
          if (response.status === 200) {
            await realmApp.emailPasswordAuth.resetPassword({
              token,
              tokenId,
              password,
            });
            return true;
          }

          return false;
        })
        .catch((err) => {
          return false;
        });

      return result;
    } catch (error) {
      return false;
    }
  };

  const checkRole = (role: string) => {
    if (user === undefined) {
      return false;
    }

    if (isExpired(role)) {
      return role === 'Expired';
    }

    const roles = user.customData.role.split(',');
    return roles.length > 0 && roles.includes(role);
  };

  const isExpired = (role: string) => {
    if (user === undefined) {
      return true;
    }

    const lastPasswordChange = parseInt(
      user.customData.lastPasswordChange.$date.$numberLong
    );
    if (isNaN(lastPasswordChange)) {
      return true;
    }

    const fromNowSinceEpoch = Date.now() - lastPasswordChange;
    const fromNowDays = fromNowSinceEpoch / 1000 / 60 / 60 / 24;
    if (fromNowDays >= 90) {
      return true;
    }
  };

  const checkUserType = (userType: string) => {
    if (user === undefined) {
      return false;
    }

    return user.customData.userType?.toLowerCase() === userType.toLowerCase();
  };

  return (
    <UserContext.Provider
      value={{
        user,
        fetchUser,
        login,
        logout,
        forgotPassword,
        changePassword,
        resetPassword,
        checkRole,
        checkUserType,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};